/**
 * useSocketHandlers - Zentralisiert alle Socket.io Event-Handler
 *
 * Extrahiert die Socket-Logik aus App.js für bessere Wartbarkeit.
 * Verwendet GameContext dispatch für State-Updates.
 *
 * REFACTORED: Nutzt jetzt useDispatchActions für die Dispatch-Wrapper.
 */

import { useCallback, useRef, useEffect } from 'react';
import socketService from '../socketService';
import soundService from '../soundService';
import { useShopHandlers, useExchangeHandlers } from './socketHandlers';
import { setupBadgeHandlers } from './socketHandlers/badgeHandlers';
import { useDispatchActions } from './useDispatchActions';
import { useNotifications } from '../components/notifications';
import { setPlayerIndex as setMinigamePlayerIndex } from '../state/minigameStore';

/**
 * @param {Object} config - Konfiguration mit dispatch und read-only state
 */
export function useSocketHandlers(config) {
  const {
    // GameContext dispatch (für zentrales State Management)
    dispatch,

    // Dependencies (read-only state from GameContext)
    players,
    myPlayerIndex,
    isLocalTestMode,
    showShop,
  } = config;

  // === Dispatch-basierte Actions (aus Hook) ===
  const actions = useDispatchActions(dispatch);

  // === Notifications (für Toasts) ===
  const { notify } = useNotifications();

  // Ref to track seen seals for animations
  const seenSealsRef = useRef(new Set());

  // Ref for peek timeout (to cancel previous timeout on new peek)
  const peekTimeoutRef = useRef(null);

  // === Shop Handlers ===
  const {
    handleShopPhaseStarted,
    handleShopPlayerChanged,
    handleSealPurchased,
    handleJokerPurchased,
    handleJokerSold,
    handleBundlePurchased,
    handleJokerBundlePurchased,
  } = useShopHandlers({
    dispatch,
    setShopItems: actions.setShopItems,
    setShopJokers: actions.setShopJokers,
    setShopJokersPerPlayer: actions.setShopJokersPerPlayer,
    setShopCurrentPlayer: actions.setShopCurrentPlayer,
    setShowShop: actions.setShowShop,
    setShowRoundEnd: actions.setShowRoundEnd,
    notify,
    myPlayerIndex,
    players,
  });

  // === Exchange Handlers (inkl. Seal Distribution) ===
  const {
    handleExchangePhaseStarted,
    handlePlayerExchangeReady,
    handleCardsPerPlayerChanged,
    handleCardsDealtForExchange,
    handleExchangeCardsDealt,
    handleExchangePlayerChanged,
    // Note: handleExchangeCardsFlying is handled in PlayingPhase
    handleExchangePhaseChanged,
    handleExchangeComplete,
    // Seal Distribution
    handleSealDistributionStarted,
    handleSealDistributionPlayerChanged,
    handleSealPlaced,
    handleSealRemoved,
    handlePlayerSealReady,
    handleSealDistributionComplete,
    handleSealDistributionTimeout,
    handleSealPlacementError,
  } = useExchangeHandlers({
    dispatch,
    players,
    setRankings: actions.setRankings,
    setExchangeInfo: actions.setExchangeInfo,
    setExchangeReady: actions.setExchangeReady,
    setExchangeCurrentPlayer: actions.setExchangeCurrentPlayer,
    setCardsPerPlayer: actions.setCardsPerPlayer,
    setExchangeCardsDealt: actions.setExchangeCardsDealt,
    setSealedCards: actions.setSealedCards,
    setShowShop: actions.setShowShop,
    setShowExchange: actions.setShowExchange,
    setGamePhase: actions.setGamePhase,
    // Note: exchangeCardsFlying is handled directly in PlayingPhase for animation
    notify, // Für Toast-Benachrichtigungen bei Kartentausch
  });

  // Note: Animation handlers (handleScoreUpdate, handlePlayerFinished, handleCardAnimation, handleSealEffects)
  // are now managed by PlayingPhase component via useAnimationHandlers hook

  // === Badge Handlers ===
  // Setup badge handlers (separate from other handlers as it only needs notify)
  useEffect(() => {
    const cleanup = setupBadgeHandlers(notify);
    return cleanup;
  }, [notify]);

  // === Core Game Handlers ===

  /**
   * Handler für gameStateUpdate Event
   */
  const handleGameStateUpdate = useCallback((data) => {
    const gs = data.gameState;

    // === GameContext Update (für Migration) ===
    if (dispatch) {
      dispatch({
        type: 'GAME_STATE_UPDATE',
        payload: {
          phase: gs.phase,
          roundNumber: gs.roundNumber,
          trickNumber: gs.trickNumber,
          numPlayers: gs.numPlayers,
          players: gs.players || gs.playerSummaries,
          currentPlayer: gs.currentPlayer,
          rankings: gs.rankings,
          myPlayerIndex: gs.myPlayerIndex,
          hands: gs.hands,
          handCounts: gs.handCounts,
          myHand: gs.myHand,
          lastPlay: gs.lastPlay,
          lastPlayPlayer: gs.lastPlayPlayer,
          drawPileCount: gs.drawPileCount,
          isFirstTurn: gs.isFirstTurn,
          canDraw: gs.canDraw,
          minCard: gs.minCard,
          consecutivePasses: gs.consecutivePasses,
          coins: gs.coins,
          points: gs.points,
          mult: gs.mult,
          jokers: gs.jokers,
          stash: gs.stash,
          sealedCards: gs.sealedCards,
          lastPlaySeals: gs.lastPlaySeals,
          shopJokers: gs.shopJokers,
          shopBoosters: gs.shopBoosters,
          shopItems: gs.shopItems,
          shopBundlePerPlayer: gs.shopBundlePerPlayer,
          shopJokersPerPlayer: gs.shopJokersPerPlayer,
          shopReady: gs.shopReady,
          shopRerollCount: gs.shopRerollCount,
          shopJokerRerollCount: gs.shopJokerRerollCount,
          shopCurrentPlayer: gs.shopCurrentPlayer,
          exchangeState: gs.exchangeState,
          exchangeReady: gs.exchangeReady,
          exchangeSelections: gs.exchangeSelections,
          exchangeCurrentPlayer: gs.exchangeCurrentPlayer,
          exchangeCardsDealt: gs.exchangeCardsDealt,
          roundStats: gs.roundStats,
          shieldUses: gs.shieldUses,
          peekUses: gs.peekUses,
          sealRingUses: gs.sealRingUses,
          magnetUses: gs.magnetUses,
          swapUses: gs.swapUses,
          middleFingerActive: gs.middleFingerActive,
          peekedCard: gs.peekedCard,
          peekedCards: gs.peekedCards,
          // Turn timer
          turnTimeLimit: gs.turnTimeLimit,
          turnStartTime: gs.turnStartTime,
          // Host card back (color from broadcast)
          hostCardBackColor: gs.hostCardBackColor,
          // Test Mode / Admin flags (from event top-level)
          isLocalTestMode: data.isLocalTestMode || gs.isLocalTestMode,
          isDevAdmin: data.isDevAdmin,
          // Game mode & victory state
          gameMode: gs.gameMode,
          gameModeTarget: gs.gameModeTarget,
          isGameComplete: gs.isGameComplete,
          gameWinner: gs.gameWinner,
          winReason: gs.winReason,
          // Private room progression (Stammtisch)
          xpProgress: gs.xpProgress,
          levelUpNotification: gs.levelUpNotification,
          sessionBadges: gs.sessionBadges,
          // Victory votes
          votes: gs.votes,
          // Betting ("Die Wette")
          bettingState: gs.bettingState,
          previousRankings: gs.previousRankings,
        },
      });
    }

    // Update player index if not set
    if (gs.myPlayerIndex !== undefined && myPlayerIndex === null) {
      actions.setMyPlayerIndex?.(gs.myPlayerIndex);
      // Also sync to minigame store for MinigameOverlay
      setMinigamePlayerIndex(gs.myPlayerIndex);
    }

    // Update economy state
    if (gs.coins) actions.setCoins?.(gs.coins);
    if (gs.points) actions.setPoints?.(gs.points);
    if (gs.mult) actions.setMult?.(gs.mult);

    // Update joker state
    if (gs.jokers) actions.setJokers?.(gs.jokers);
    if (gs.shopJokers) actions.setShopJokers?.(gs.shopJokers);
    if (gs.shopJokersPerPlayer) actions.setShopJokersPerPlayer?.(gs.shopJokersPerPlayer);

    // Update seal state
    if (gs.stash) actions.setStash?.(gs.stash);
    if (gs.sealedCards) actions.setSealedCards?.(gs.sealedCards);

    // Update phase state
    if (gs.phase) {
      actions.setGamePhase?.(gs.phase);
      if (gs.phase !== 'exchanging') {
        actions.setShowExchange(false);
        actions.setExchangeInfo(null);
      }
    }
    if (gs.roundNumber) actions.setRoundNumber?.(gs.roundNumber);
    if (gs.trickNumber) actions.setTrickNumber?.(gs.trickNumber);

    // Update shop state
    if (gs.shopItems) actions.setShopItems?.(gs.shopItems);
    if (gs.shopReady) actions.setShopReady?.(gs.shopReady);
    if (gs.shopCurrentPlayer !== undefined) actions.setShopCurrentPlayer?.(gs.shopCurrentPlayer);
    if (gs.shopRerollCount) actions.setShopRerollCount?.(gs.shopRerollCount);
    if (gs.shopJokerRerollCount) actions.setShopJokerRerollCount?.(gs.shopJokerRerollCount);

    // Update ability state
    if (gs.shieldUses) actions.setShieldUses?.(gs.shieldUses);
    if (gs.peekUses) actions.setPeekUses?.(gs.peekUses);
    if (gs.sealRingUses) actions.setSealRingUses?.(gs.sealRingUses);
    if (gs.magnetUses) actions.setMagnetUses?.(gs.magnetUses);
    if (gs.swapUses) actions.setSwapUses?.(gs.swapUses);

    // Update exchange state
    if (gs.exchangeReady) actions.setExchangeReady?.(gs.exchangeReady);
    if (gs.exchangeCurrentPlayer !== undefined) actions.setExchangeCurrentPlayer?.(gs.exchangeCurrentPlayer);

    // Update round stats
    if (gs.roundStats) actions.setRoundStats?.(gs.roundStats);
    if (gs.minCard) actions.setMinCard?.(gs.minCard);
    if (gs.rankings) actions.setRankings?.(gs.rankings);

    // Update round starter flag
    const currentPlayerIdx = isLocalTestMode ? gs.currentPlayer : (gs.myPlayerIndex !== undefined ? gs.myPlayerIndex : myPlayerIndex);
    const isStartingFresh = gs.isFirstTurn || gs.lastPlayPlayer === currentPlayerIdx;
    actions.setIsRoundStarter?.(isStartingFresh);

    // Handle phase changes
    if (gs.phase === 'shop' && !showShop) {
      actions.setShowShop(true);
      actions.setShowRoundEnd(false);
    } else if (gs.phase === 'playing') {
      actions.setShowShop(false);
      actions.setShowRoundEnd(false);
      actions.setShowExchange(false);
    }

    // Handle exchangeState updates
    if (gs.exchangeState !== undefined) {
      actions.setExchangeState?.(gs.exchangeState);
    }

    // Track last action for status messages
    if (gs.lastAction) {
      actions.setLastAction(gs.lastAction);
      actions.setLastActionPlayer(gs.lastActionPlayer);
      actions.setLastPlaySize(gs.lastPlaySize || 0);
    }
  }, [
    dispatch, myPlayerIndex, isLocalTestMode, showShop, actions
  ]);

  /**
   * Handler für roundEnded Event
   * Integriert roundEndJokerBonuses in roundStats für ScoringSlide-Anzeige
   */
  const handleRoundEnded = useCallback((data) => {
    soundService.play('round_end');

    // Merge roundEndJokerBonuses into roundStats per player
    // Server sends: roundEndJokerBonuses: [{playerIndex, points, effects}, ...]
    // Client expects: roundStats[i].roundEndJokerBonus, roundStats[i].roundEndJokerEffects
    let enrichedRoundStats = data.roundStats;
    if (data.roundEndJokerBonuses && data.roundEndJokerBonuses.length > 0) {
      enrichedRoundStats = data.roundStats.map((stats, idx) => {
        const jokerBonus = data.roundEndJokerBonuses.find(b => b.playerIndex === idx);
        if (jokerBonus) {
          return {
            ...stats,
            roundEndJokerBonus: jokerBonus.points,
            roundEndJokerEffects: jokerBonus.effects || []
          };
        }
        return stats;
      });
    }

    actions.setPendingRoundEnd({
      rankings: data.rankings,
      roundStats: enrichedRoundStats,
      roundIncome: data.roundIncome,
      points: data.points,
      coins: data.coins,
      mult: data.mult,
      roundNumber: data.roundNumber
    });
  }, [actions]);

  /**
   * Handler für roundEnding Event (neue choreographierte Endsequenz)
   * Startet die dramatische Schlussanimation wenn die letzte Karte gespielt wird
   */
  const handleRoundEnding = useCallback((data) => {
    dispatch?.({
      type: 'START_END_SEQUENCE',
      payload: {
        lastCard: data.lastCard,
        lastPlay: data.lastPlay,
        finishers: data.finishers,
        startTimestamp: data.startTimestamp
      }
    });
  }, [dispatch]);

  /**
   * Handler für gameEnded Event
   */
  const handleGameEnded = useCallback((data) => {
    if (data.rankings) {
      actions.setRankings?.(data.rankings);
      dispatch?.({ type: 'SET_RANKINGS', payload: data.rankings });
    }
  }, [dispatch, actions]);

  /**
   * Handler für gameComplete Event (Game Mode Win Condition)
   */
  const handleGameComplete = useCallback((data) => {
    console.log('[useSocketHandlers] Game complete!', data);
    soundService.play('game_win');

    dispatch?.({
      type: 'GAME_STATE_UPDATE',
      payload: {
        phase: 'victory',
        isGameComplete: true,
        gameWinner: data.winner,
        gameWinnerName: data.winnerName,
        winReason: data.winReason,
        points: data.finalPoints,
        coins: data.finalCoins,
        gameMode: data.gameMode,
        gameModeTarget: data.gameModeTarget || data.targetPoints,
        immediateVictory: data.immediate || false,
      },
    });

    actions.setShowShop(false);
    actions.setShowRoundEnd(false);
  }, [dispatch, actions]);

  /**
   * Handler für error Event
   */
  const handleError = useCallback((error) => {
    soundService.play('error');
    console.error('[Socket] Error:', error.message || 'Fehler aufgetreten');
  }, []);

  /**
   * Handler für peekResult Event (Sneak Peak Siegel)
   */
  const handlePeekResult = useCallback((data) => {
    // Cancel previous timeout to prevent race condition on multiple peeks
    if (peekTimeoutRef.current) {
      clearTimeout(peekTimeoutRef.current);
    }

    actions.setPeekedCard(data.card);

    peekTimeoutRef.current = setTimeout(() => {
      actions.setPeekedCard(null);
      peekTimeoutRef.current = null;
    }, 3000);
  }, [actions]);

  /**
   * Handler für hostCardBack Event (shared card back from host)
   */
  const handleHostCardBack = useCallback((data) => {
    dispatch({ type: 'SET_HOST_CARD_BACK', payload: { image: data.image, color: data.color } });
  }, [dispatch]);

  /**
   * Registriert alle Socket-Listener
   */
  const registerListeners = useCallback(() => {
    // Core game
    socketService.onGameStateUpdate(handleGameStateUpdate);
    socketService.onRoundEnding(handleRoundEnding);
    socketService.onRoundEnded(handleRoundEnded);
    socketService.onGameEnded(handleGameEnded);
    socketService.registerListener('gameComplete', handleGameComplete);
    socketService.onError(handleError);
    socketService.onPeekResult(handlePeekResult);
    socketService.registerListener('hostCardBack', handleHostCardBack);

    // Shop
    socketService.onShopPhaseStarted(handleShopPhaseStarted);
    socketService.onShopPlayerChanged(handleShopPlayerChanged);
    socketService.onSealPurchased(handleSealPurchased);
    socketService.onJokerPurchased(handleJokerPurchased);
    socketService.onJokerSold(handleJokerSold);
    socketService.onBundlePurchased(handleBundlePurchased);
    socketService.onJokerBundlePurchased(handleJokerBundlePurchased);

    // Exchange
    socketService.onExchangePhaseStarted(handleExchangePhaseStarted);
    socketService.onPlayerExchangeReady(handlePlayerExchangeReady);
    socketService.onCardsPerPlayerChanged(handleCardsPerPlayerChanged);
    socketService.onCardsDealtForExchange(handleCardsDealtForExchange);
    socketService.onExchangePlayerChanged(handleExchangePlayerChanged);
    socketService.registerListener('exchangeCardsDealt', handleExchangeCardsDealt);
    // Note: exchangeCardsFlying is handled in PlayingPhase for animation
    socketService.registerListener('exchangePhaseChanged', handleExchangePhaseChanged);
    socketService.registerListener('exchangeComplete', handleExchangeComplete);

    // Seal Distribution
    socketService.registerListener('sealDistributionStarted', handleSealDistributionStarted);
    socketService.registerListener('sealDistributionPlayerChanged', handleSealDistributionPlayerChanged);
    socketService.registerListener('sealPlaced', handleSealPlaced);
    socketService.registerListener('sealRemoved', handleSealRemoved);
    socketService.registerListener('playerSealReady', handlePlayerSealReady);
    socketService.registerListener('sealDistributionComplete', handleSealDistributionComplete);
    socketService.registerListener('sealDistributionTimeout', handleSealDistributionTimeout);
    socketService.registerListener('sealPlacementError', handleSealPlacementError);

    // Note: Animation handlers are registered in PlayingPhase component
  }, [
    // Core
    handleGameStateUpdate, handleRoundEnding, handleRoundEnded, handleGameEnded, handleGameComplete, handleError, handlePeekResult, handleHostCardBack,
    // Shop
    handleShopPhaseStarted, handleShopPlayerChanged, handleSealPurchased,
    handleJokerPurchased, handleJokerSold, handleBundlePurchased, handleJokerBundlePurchased,
    // Exchange
    handleExchangePhaseStarted, handlePlayerExchangeReady, handleCardsPerPlayerChanged,
    handleCardsDealtForExchange, handleExchangePlayerChanged,
    handleExchangeCardsDealt, handleExchangePhaseChanged, handleExchangeComplete,
    // Seal Distribution
    handleSealDistributionStarted, handleSealDistributionPlayerChanged, handleSealPlaced, handleSealRemoved,
    handlePlayerSealReady, handleSealDistributionComplete, handleSealDistributionTimeout, handleSealPlacementError,
  ]);

  /**
   * Entfernt alle Socket-Listener
   */
  const unregisterListeners = useCallback(() => {
    // Clean up peek timeout
    if (peekTimeoutRef.current) {
      clearTimeout(peekTimeoutRef.current);
      peekTimeoutRef.current = null;
    }

    socketService.off('gameStateUpdate');
    socketService.off('roundEnding');
    socketService.off('roundEnded');
    socketService.off('gameEnded');
    socketService.off('gameComplete');
    socketService.off('error');
    socketService.off('peekResult');
    socketService.off('hostCardBack');
    socketService.off('shopPhaseStarted');
    socketService.off('shopPlayerChanged');
    socketService.off('sealPurchased');
    socketService.off('jokerPurchased');
    socketService.off('jokerSold');
    socketService.off('bundlePurchased');
    socketService.off('jokerBundlePurchased');
    socketService.off('exchangePhaseStarted');
    socketService.off('playerExchangeReady');
    socketService.off('cardsPerPlayerChanged');
    socketService.off('cardsDealtForExchange');
    socketService.off('exchangePlayerChanged');
    socketService.off('exchangeCardsDealt');
    // Note: exchangeCardsFlying is handled in PlayingPhase
    socketService.off('exchangePhaseChanged');
    socketService.off('exchangeComplete');
    // Seal Distribution
    socketService.off('sealDistributionStarted');
    socketService.off('sealDistributionPlayerChanged');
    socketService.off('sealPlaced');
    socketService.off('sealRemoved');
    socketService.off('playerSealReady');
    socketService.off('sealDistributionComplete');
    socketService.off('sealDistributionTimeout');
    socketService.off('sealPlacementError');
    // Note: Animation listeners are unregistered in PlayingPhase component
  }, []);

  return {
    registerListeners,
    unregisterListeners,
    seenSealsRef,
  };
}

export default useSocketHandlers;
